import React from 'react';
import { useFetcher } from 'react-router';
import { useDebounce } from 'ahooks';
import { useProductTagging } from '~/modules/product-tagging/context';
import type { TagType } from '~/modules/product-tagging/type';
import useProductTileBottomActions from '~/modules/product-tagging/hooks/use-product-tile-bottom-actions';
import { TAG_CONFIRMATION_MESSAGE } from '../../../config/search-form';
import generateFetcherKey from '~/utils/generate-fetcher-key';

interface UseTagModalsProps {
    marker: TagType;
    deleteTag: (tagId: number) => void;
    onApproveClicked?: () => void;
    onDeleteClicked?: () => void;
}
export const useTagModals = ({
    marker,
    deleteTag,
    onApproveClicked,
    onDeleteClicked,
}: UseTagModalsProps) => {
    const fetcher = useFetcher<never>();
    const [isOpen, setIsOpen] = React.useState(false);
    const [forceClosed, setForceClosed] = React.useState(false);
    const debouncedOpen = useDebounce(isOpen, { wait: 100 });
    const effectiveOpen = debouncedOpen && !forceClosed;

    React.useEffect(() => {
        if (isOpen) setForceClosed(false);
    }, [isOpen]);

    const forceClose = React.useCallback(() => {
        setIsOpen(false);
        setForceClosed(true);
    }, []);

    const {
        state: { isDeletingTag, isAddingSupplier, lastAddedTag },
        canDeleteTag,
    } = useProductTagging();

    const isTogglingApproved =
        fetcher?.formData?.get('tagId') === String(marker.id);
    const isCompletedTogglingApproved = fetcher.state === 'loading';

    const isMarkerApproved = marker.approved;

    const onToggleApproved = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const formData = new FormData();
        formData.set('tagId', String(marker.id));
        formData.set('approved', String(!marker.approved));
        formData.set('fetcherKey', generateFetcherKey());
        fetcher.submit(formData, {
            method: 'post',
            encType: 'multipart/form-data',
            action: '/remix-api/product-tagging/update-library-image-marker',
        });
        onApproveClicked?.();
    };

    const onDeleteTag = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        deleteTag(marker.id);
        onDeleteClicked?.();
    };

    const bottomActions = useProductTileBottomActions({
        approved: isMarkerApproved,
        onClickThumb: onToggleApproved,
        disableThumb: isTogglingApproved,
        onClickBin: onDeleteTag,
        disableBin: isDeletingTag,
    });

    const hasProduct = marker.product?.id;
    const professionalForUnknownProduct = marker.professionalForUnknownProduct;
    const titleForUnknownProfessional = marker.titleForUnknownProfessional;

    const canShowProductTile = canDeleteTag || isMarkerApproved;

    const showModal =
        (effectiveOpen &&
            ((hasProduct && !canShowProductTile) ||
                professionalForUnknownProduct?.id ||
                marker.titleForUnknownProfessional)) ||
        // Adding a new supplier (through form or search)
        (isAddingSupplier &&
            (marker.titleForUnknownProfessional ||
                professionalForUnknownProduct?.id) &&
            lastAddedTag &&
            marker.positionX === lastAddedTag.pctX &&
            marker.positionY === lastAddedTag.pctY);

    const confirmationMessage =
        professionalForUnknownProduct?.title || titleForUnknownProfessional ? (
            <>
                A product supplied by{' '}
                <strong>
                    “
                    {professionalForUnknownProduct?.title ||
                        titleForUnknownProfessional}
                    ”
                </strong>{' '}
                was tagged in this image. We’re taking a look to see if we can
                locate the product and approve it.
            </>
        ) : (
            TAG_CONFIRMATION_MESSAGE
        );

    return {
        isModalOpen: effectiveOpen,
        isMarkerApproved,
        isTogglingApproved,
        isCompletedTogglingApproved,
        showModal,
        canShowProductTile,
        bottomActions,
        hasProduct,
        confirmationMessage,
        onDeleteTag,
        onToggleApproved,
        setIsOpen,
        forceClose,
    };
};
